<template>
  <footer class="footer">
    <div class="footer-container">
      <div class="top-container">
        <div class="items">
          <b>{{ aboutUsLink.title }}</b>
          <router-link v-if="currentLocale === 'de'" :to="{ name: faqLink.name }" lang="en">
            {{ faqLink.title }}
          </router-link>
          <a :href="aboutUsLink.url" target="_blank">
            {{ aboutUsLink.title }}
            <span class="sr-only">{{$t("general.openInNewTab")}}</span>
            <svg xmlns="http://www.w3.org/2000/svg" class="open-in-new" aria-hidden="true" focusable="false" width="24" height="24" viewBox="0 0 24 24"
                 fill="none">
              <mask id="mask0_149_1328" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="24"
                    height="24">
                <rect width="24" height="24" fill="#D9D9D9"/>
              </mask>
              <g mask="url(#mask0_149_1328)">
                <path
                  d="M5 21C4.45 21 3.979 20.8043 3.587 20.413C3.19567 20.021 3 19.55 3 19V5C3 4.45 3.19567 3.979 3.587 3.587C3.979 3.19567 4.45 3 5 3H12V5H5V19H19V12H21V19C21 19.55 20.8043 20.021 20.413 20.413C20.021 20.8043 19.55 21 19 21H5ZM9.7 15.7L8.3 14.3L17.6 5H14V3H21V10H19V6.4L9.7 15.7Z"
                  fill="white"/>
              </g>
            </svg>
          </a>
        </div>
        <div v-if="currentLocale === 'de'" class="items">
          <b>{{ $t("general.followUs") }}</b>
          <div class="social-items">
            <a v-for="link in getLinksSocial()" :key="link.name" target="_blank" :href="link.url">
              <img
                :src="require(`@/assets/icons/socialmedia/${link.name}.svg`)"
                :alt="`${link.name} icon`"
                :aria-label="link.name"
              />
            </a>
          </div>
        </div>
      </div>
      <hr />
      <div class="bottom-container">
        <router-link :to="{ name: imprintLink.name }">{{ imprintLink.title }}</router-link>
        <router-link :to="{ name: privacyLink.name }">{{ privacyLink.title }}</router-link>
        <a href="javascript:void(0)" @click.prevent="openCookiePreferences"><p v-html="$t('general.cookieSettings')"></p></a>
        <router-link :to="{ name: accessibilityLink.name }">{{ accessibilityLink.title }}</router-link>
      </div>
    </div>
  </footer>
</template>


<script lang="ts">
import { defineComponent } from "vue";
import i18n, {getLocale} from "@/i18n/index";
import { mapState } from "vuex";
import { getRouteName } from "@/routeConfig";
import { UsercentricsConsentService } from "@/services/UsercentricsConsentService";

interface InternalLink {
  title: string;
  name?: string;
  url?: string;
}
interface InternalLinkWithName extends InternalLink {
  name: string;
}
interface InternalLinkWithUrl extends InternalLink {
  url: string;
}

interface SocialLink {
  name: string;
  url: string;
}

declare const OneTrust: any;

export default defineComponent({
  data() {
    return {
      // Rhenus social media channels; besides using the name as a key, it's used to
      // gather the social media icon dynamically from assets/icons/socialmedia
      // make sure, there's a corresponding svg icon there.
      linksSocial: [
        {
          name: "linkedin",
          url: "https://www.linkedin.com/company/rhenus-home-delivery-gmbh/?viewAsMember=true",
        },
      ] as SocialLink[],
    };
  },
  created() {
  },
  computed: {
    ...mapState(["language"]),
    currentLocale(): string {
      return getLocale();
    },
    imprintLink(): InternalLinkWithName {
      return {
        title: this.$t("general.impressum"),
        name: getRouteName("imprint"),
      };
    },
    privacyLink(): InternalLinkWithName {
      return {
        title: this.$t("general.privacy"),
        name: getRouteName("privacy"),
      };
    },
    accessibilityLink(): InternalLinkWithName {
      return {
        title: this.$t("general.accessibility"),
        name: getRouteName("accessibility"),
      };
    },
    aboutUsLink(): InternalLinkWithUrl {
      let url = "https://www.rhenus.group/customised-supply-chain/home-delivery";
      const currentLocale = this.currentLocale;
      if (currentLocale === "de") {
        url = "https://www.rhenus.group/de/customised-supply-chain/home-delivery";
      } else if (currentLocale === "pl") {
        url = "https://www.rhenus.com/pl/pl/uslugi/home-delivery";
      }
      return {
        title: this.$t("general.aboutUs"),
        url: url,
      };
    },
    faqLink(): InternalLinkWithName {
      return {
        title: this.$t("general.faq"),
        name: getRouteName("faq"),
      };
    },
    linksInternal(): InternalLink[] {
      return [
        this.imprintLink,
        this.privacyLink,
        this.aboutUsLink,
        this.faqLink,
      ];
    },
  },
  watch: {
    language(actualLanguage) {
      if (actualLanguage === "de") {
        this.aboutUsLink.url =
          "https://www.rhenus.group/de/customised-supply-chain/home-delivery/";
      } else if (actualLanguage === "pl") {
        this.aboutUsLink.url =
          "https://www.rhenus.com/pl/pl/uslugi/home-delivery";
      } else {
        this.aboutUsLink.url =
          "https://www.rhenus.group/customised-supply-chain/home-delivery";
      }
    },
  },
  methods: {
    getLinksInternal() {
      if (i18n.global.locale === "de") {
        return this.linksInternal;
      } else {
        return [
          this.linksInternal[0],
          this.linksInternal[1],
          this.linksInternal[2],
        ];
      }
    },
    getLinksSocial() {
      if (i18n.global.locale === "de") {
        return this.linksSocial;
      } else {
        return [];
      }
    },
    showCookieSettings() {
      OneTrust.ToggleInfoDisplay();
    },
    openCookiePreferences() {
      UsercentricsConsentService.openCookiePreferences();
    }
  },
});
</script>


<style lang="scss" scoped>
.footer {
  color: $color-secondary-bluegray-25;
  background-color: $color-primary-blue;

  a:focus {
    outline-color: #fff;
  }

  &-container {
    display: flex;
    flex-direction: column;
    padding: $layout-size-padding;
    max-width: $layout-size;
    gap: 40px;
    color: #FFF;
    margin: auto;

    a:hover {
      font-weight: bold;
      text-decoration: underline;
    }

    a:focus {
      outline: 2px solid #FFF;
    }

    .top-container {
      display: flex;
      justify-content: space-between;
      flex-wrap: wrap;
      gap: 15px;

      .social-items {
        display: flex;
        gap: 20px;
        align-items: center;
      }

      .items {
        display: flex;
        flex-direction: column;
        gap: 16px;

        svg {
          margin-bottom: -4px;
        }
      }
    }

    hr {
      border: 1px solid #FFF;
    }

    .bottom-container {
      display: flex;
      gap: 24px;
      flex-direction: column;

      @include fit(480) {
        flex-direction: row;
      }
    }
  }
}
</style>
